import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { StateService } from '@uirouter/core';
import * as _ from 'lodash';
import { ControlService } from '../control/control.service';
import { DeviceService } from '../shared/services/device.service';
import { DeviceInformation } from '../shared/models/device-information';
import { DeviceModelService } from '../shared/services/device-model.service';
import { DeviceChangeConfirmationComponent } from '../device-change-confirmation/device-change-confirmation.component';
import { TemplateChoiceDialogComponent } from '../template-choice-dialog/template-choice-dialog.component';
import { TemplateNameDialogComponent } from '../template-name-dialog/template-name-dialog.component';
import { UpdateConfirmationDialogComponent } from '../update-confirmation-dialog/update-confirmation-dialog.component';
import { IDeviceInformation, IDeviceNode } from '../shared/models/device';
import { DeviceSettingsBaseComponent } from '../device-settings-base/device-settings-base.component';
import { SystemSettingsService } from './system-settings.service';
import { DialogService } from '@caslink/common-client/ng-app/dialog-service/dialog.service';
import { MessageType } from '@caslink/common-client';
import { UserPermissionService } from '@caslink/common-client/ng-app/userPermission/UserPermissionService';
import { CaalFileDialogComponent } from '../caal-file-dialog/caal-file-dialog.component';
import { IJobDocumentTreeNodeViewModel } from '../shared/models/nola-job-document-models';
import { DashboardToastrConfirmationService } from '../shared/services/dashboard-toastr-confirmation.service';
import { DeviceUpdateAction } from '../shared/models/models';

@Component({
    selector: 'dl-system-settings',
    templateUrl: './system-settings.component.html'
})
export class SystemSettingsComponent extends DeviceSettingsBaseComponent implements OnInit {
    public static $name: string = 'systemSettings';

    @Input() public updateWarning: string;

    public systemSettingsForm: FormGroup;
    canManageTemplates: boolean;
    canImportFromCAALFile: boolean;
    templateApplied: boolean;
    public hasCAALManagementPermission: Boolean = false;
    public isCommissionerModelSupported: Boolean = false;


    constructor(
        public cdr: ChangeDetectorRef,
        private confirmationDialogService: DialogService,
        private deviceService: DeviceService,
        private systemSettingsService: SystemSettingsService,
        protected $state: StateService,
        protected matDialog: MatDialog,
        private dialogService: DialogService,
        protected controlService: ControlService,
        protected deviceModelService: DeviceModelService,
        private userPermissionService: UserPermissionService,
        protected dashboardToastrConfirmationService: DashboardToastrConfirmationService,
    ) {
        super($state, deviceModelService, controlService, matDialog, dashboardToastrConfirmationService);
    }

    public ngOnInit() {
        this.modelName = 'Factory';

        this.systemSettingsForm = new FormGroup({});

        this.getHasCAALManagementPermission();

        this.SetCanManageTemplates();

        this.loadDevice();

    }

    public selectTemplate() {


        const dialogRef = this.dialogService.showDialogView(TemplateChoiceDialogComponent, {
            data: {
                category: (this.deviceInformation.$deviceHierarchy.identifier as any).actorType,
                templateType: 'SystemInformation',
                locationId: this.locationId
            }
        });

        dialogRef.afterClosed().toPromise().then((result: string) => {
            if (result != null) {

                this.systemSettingsForm = new FormGroup({});

                this.isLoaded = false;

                this.systemSettingsService.applyDeviceInformationTemplate(this.deviceInformation, this.hardwareItemId, result).then((node: IDeviceNode) => {
                    this.deviceInformation = this.deviceService.setupDeviceInfo(node);
                    this.originalDeviceInformation = _.cloneDeep(this.deviceInformation);
                    this.isLoaded = true;
                    this.templateApplied = true;
                });

            }
        });

    }

    public makeTemplate() {

        const hasChanges: boolean = !((!this.systemSettingsForm.dirty || this.systemSettingsForm.invalid) && !this.templateApplied);

        const dialogRef = this.matDialog.open(TemplateNameDialogComponent, {
            height: '250px',
            data: {
                template: '',
                fullsnapshot: !hasChanges
            }
        });

        dialogRef.afterClosed().toPromise().then((result: any) => {
            if (result != null) {

                this.systemSettingsService.saveDeviceInformationTemplate(this.deviceInformation, this.hardwareItemId, result.template, result.fullsnapshot).then(() => {
                    // do nothing
                });

            }
        });
    }

    public confirmUpdateDeviceInfo() {
        const dialogRef = this.confirmationDialogService.showConfirmDialog({
            data: {
                title: this.modelName,
                message: this.getUpdateWarning(),
                messageType: MessageType.Warning
            }
        });

        dialogRef.afterClosed()
            .toPromise()
            .then(confirmed => {
                if (confirmed) {
                    this.updateDeviceInfo(null);
                }
            });
    }


    public confirmUpdateDeviceInfoEx() {
        this.userPermissionService.getHasAccess('Settings.NoteRequiredForControlUpdate')
            .then((hasAccess: boolean) => {
                if (hasAccess) {


                    this.systemSettingsService.previewDeviceInformationTemplate(this.deviceInformation, this.hardwareItemId, "preview", false).then((preview) => {

                        const dialogRef = this.matDialog.open(UpdateConfirmationDialogComponent, {
                            data: {
                                dialogConfirmOptions: {
                                    title: this.modelName,
                                    message: this.getUpdateWarning(),
                                    messageType: MessageType.Warning
                                },
                                preview: preview
                            }
                        });



                        dialogRef.afterClosed().toPromise().then((result: string) => {
                            if (result != null) {

                                this.updateDeviceInfo(result);

                            }
                        });

                    });

                } else {
                    this.confirmUpdateDeviceInfo();
                }
            });
    }

    public updateDeviceInfo(note: string) {
        this.updateDevice(this.loadDevice, DeviceUpdateAction.DEVICE_INFORMATION, note);
    }

    public setDeviceInfo = (deviceInfo: DeviceInformation) => {
        this.isLoaded = false;
        this.cdr.detectChanges();

        const isDirty = this.systemSettingsForm.dirty;
        // recreate the form to prevent old data and related errors from persisting.
        this.systemSettingsForm = new FormGroup({});

        this.deviceInformation = deviceInfo;
        this.isLoaded = true;
        this.cdr.detectChanges();

        if (isDirty) {
            this.systemSettingsForm.markAsDirty();
            this.systemSettingsForm.markAsTouched();
        }
    }

    private SetCanManageTemplates() {
        this.systemSettingsService.getCanManageTemplates().then((result) => {
            this.canManageTemplates = result;
        });
    }

    private getUpdateWarning(): string {
        let defaultWarning = 'This action may trigger a system reboot at the site.';
        return defaultWarning + (this.updateWarning ? ` ${this.updateWarning}.` : '') + ' Are you sure?';
    }

    // Delegate to the device service to load the system information.
    private loadDevice(): angular.IPromise<IDeviceInformation> {
        this.isLoaded = false;

        return this.systemSettingsService.getDeviceInformation(this.hardwareItemId).then((node: IDeviceNode) => {
            this.deviceInformation = this.deviceService.setupDeviceInfo(node);
            this.canImportFromCAALFile = this.deviceInformation.$deviceHierarchy.jobNumber != null;
            this.originalDeviceInformation = _.cloneDeep(this.deviceInformation);
            this.isLoaded = true;

            this.templateApplied = false;

            this.SetIsCommissionerModelSupported();

            return this.deviceInformation;
        });
    }

    public onImportFromCAALFileClick() {
        const dialogRef = this.matDialog.open(CaalFileDialogComponent, {
            data: {
                caption: 'Please select Caal file for import',
                hardwareItemId: this.deviceInformation.$deviceHierarchy.identifier.entityId,
                selectedNodeData: null
            }
        });

        dialogRef.afterClosed().toPromise().then((result: any) => {
            if (result != null && result.selectedNodeData != null) {
                const selectedNodeData: IJobDocumentTreeNodeViewModel = result.selectedNodeData;


                this.systemSettingsForm = new FormGroup({});

                this.isLoaded = false;

                this.systemSettingsService.importCaalToDeviceInformation(this.deviceInformation, this.hardwareItemId, selectedNodeData.documentId).then((node: IDeviceNode) => {
                    this.deviceInformation = this.deviceService.setupDeviceInfo(node);
                    this.originalDeviceInformation = _.cloneDeep(this.deviceInformation);
                    this.isLoaded = true;
                    this.templateApplied = true;
                });

            }
        });
    }

    private getHasCAALManagementPermission() {
        this.userPermissionService.getHasAccess('Controls.AllowCAALManagement')
            .then((hasAccess: boolean) => {
                this.hasCAALManagementPermission = hasAccess;


            });

    }

    private SetIsCommissionerModelSupported() {
        this.systemSettingsService.getIsCommissionerModelSupported(this.deviceInformation).then((result) => {
            this.isCommissionerModelSupported = result;
        });
    }

}
